/*!

=========================================================
* Light Bootstrap Dashboard React - v2.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/light-bootstrap-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect } from "react";
import { useLocation, NavLink, useHistory } from "react-router-dom";
import { Nav } from "react-bootstrap";
import { connect } from "react-redux";
import { getTranslate, LANGUAGE_KEYS } from "../../utils";
import { getToken } from "../../utils/token";
import { hasAccess } from "../../routes";
import logo from "assets/img/TKT1_logo3.png";
import { IconUser, IconBuilding, IconUserCircle, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { getPlatformDisplayName, getPlatformColors, getCurrentPlatform } from "../../utils/platform";
import { getCompanyByCompanyID } from "../../services/companyService";
import { getApiUrl } from "../../utils/platform";

// Helper function to render icon components
const renderIcon = (IconComponent, props = {}) => {
  if (!IconComponent) return null;
  // Increase size and adjust strokeWidth if desired
  const defaultProps = { size: 24, strokeWidth: 1.5 }; 
  return <IconComponent {...defaultProps} {...props} />;
};

function getGreeting(hour) {
  if (hour >= 5 && hour < 12) {
    return { text: 'Chào buổi sáng', icon: '☀️' };
  } else if (hour >= 12 && hour < 18) {
    return { text: 'Chào buổi chiều', icon: '🌤️' };
  } else if (hour >= 18 && hour < 23) {
    return { text: 'Chào buổi tối', icon: '🌙' };
  } else {
    return { text: 'Chúc ngủ ngon', icon: '😴' };
  }
}

function Sidebar({ color, image, routes, user }) {
  const location = useLocation();
  const history = useHistory();
  const [openItems, setOpenItems] = useState({});
  const [collapsedTitles, setCollapsedTitles] = useState({});
  const userGroups = user?.groups || [];
  const [currentCompany, setCurrentCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [randomGradient, setRandomGradient] = useState('');
  const [magicMode, setMagicMode] = useState(false);
  const [vantaEffect, setVantaEffect] = useState(null);

  // Set gradient to match company banner style
  useEffect(() => {
    setRandomGradient('linear-gradient(90deg,rgb(95, 156, 240) 0%,rgb(152, 111, 227) 80%)');
  }, []);

  // Load magic mode from localStorage
  useEffect(() => {
    // Only load magic mode if Vanta is enabled via environment variable
    if (process.env.REACT_APP_VANTA === '1') {
      const savedMagicMode = localStorage.getItem('sidebar-magic-mode');
      if (savedMagicMode === 'true') {
        setMagicMode(true);
      }
    }
  }, []);

  // Load collapsed titles from localStorage
  useEffect(() => {
    const savedCollapsedTitles = localStorage.getItem('sidebar-collapsed-titles');
    if (savedCollapsedTitles) {
      try {
        setCollapsedTitles(JSON.parse(savedCollapsedTitles));
      } catch (error) {
        console.error('Error parsing collapsed titles from localStorage:', error);
      }
    }
  }, []);

  // Load open items from localStorage
  useEffect(() => {
    const savedOpenItems = localStorage.getItem('sidebar-open-items');
    if (savedOpenItems) {
      try {
        setOpenItems(JSON.parse(savedOpenItems));
      } catch (error) {
        console.error('Error parsing open items from localStorage:', error);
      }
    }
  }, []);

  // Toggle magic mode when magicMode changes
  useEffect(() => {
    // Only proceed if Vanta is enabled via environment variable
    if (process.env.REACT_APP_VANTA !== '1') {
      return;
    }
    
    console.log('Magic mode changed:', magicMode);
    if (magicMode) {
      console.log('Enabling magic mode...');
      initVantaHalo();
    } else {
      console.log('Disabling magic mode...');
      destroyVantaHalo();
    }
  }, [magicMode]);

  // Time state for greeting
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000); // update every minute
    return () => clearInterval(interval);
  }, []);

  // Load current company info
  useEffect(() => {
    const loadCurrentCompany = async () => {
      if (user?.company_id && !currentCompany && !companyLoading) {
        setCompanyLoading(true);
        try {
          const token = getToken();
          console.log('DEBUG: Loading company info for company_id:', user.company_id);
          const response = await fetch(`${getApiUrl()}/api/company/company/${user.company_id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            console.log('DEBUG: Company API response:', data);
            if (data.status === 1 && data.data) {
              console.log('DEBUG: Setting current company:', data.data);
              setCurrentCompany(data.data);
            }
          } else {
            console.error('DEBUG: API response not ok:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('Error loading company info:', error);
        } finally {
          setCompanyLoading(false);
        }
      }
    };

    loadCurrentCompany();
  }, [user?.company_id, currentCompany, companyLoading]);

  // Cleanup Vanta.js on unmount
  useEffect(() => {
    return () => {
      if (vantaEffect) {
        vantaEffect.destroy();
      }
    };
  }, [vantaEffect]);

  const hour = now.getHours();
  const minute = now.getMinutes();
  const greeting = getGreeting(hour);
  const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };

  // Check if user can access my-company
  const canAccessMyCompany = userGroups.includes('Admin') || userGroups.includes('Root') || userGroups.includes('root');

  const handleCompanyBadgeClick = () => {
    if (canAccessMyCompany) {
      history.push('/admin/my-company');
    }
  };

  const handleUserIconClick = () => {
    history.push('/admin/my-account');
  };

  // Magic mode functions
  const toggleMagicMode = () => {
    // Check if Vanta is enabled via environment variable
    if (process.env.REACT_APP_VANTA !== '1') {
      console.log('Vanta is disabled via REACT_APP_VANTA environment variable');
      return;
    }
    
    const newMagicMode = !magicMode;
    console.log('Toggling magic mode:', newMagicMode);
    setMagicMode(newMagicMode);
    localStorage.setItem('sidebar-magic-mode', newMagicMode.toString());
  };

  const initVantaHalo = async () => {
    try {
      console.log('Initializing Vanta.js HALO...');
      // Load Vanta.js scripts if not already loaded
      await loadVantaScripts();
      
      console.log('Scripts loaded, checking window objects:', {
        VANTA: !!window.VANTA,
        THREE: !!window.THREE
      });
      
      if (window.VANTA && window.THREE) {
        console.log('Creating Vanta.js HALO effect...');
        const effect = window.VANTA.HALO({
          el: ".sidebar-background",
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          backgroundColor: 0x1a1a2e,
          baseColor: 0x16213e,
          size: 0.8,
          amplitudeFactor: 1.5,
          xOffset: 0,
          yOffset: 0
        });
        setVantaEffect(effect);
        console.log('Vanta.js HALO effect created successfully');
      } else {
        console.error('VANTA or THREE not available');
      }
    } catch (error) {
      console.error('Failed to initialize Vanta.js:', error);
    }
  };

  const destroyVantaHalo = () => {
    if (vantaEffect) {
      vantaEffect.destroy();
      setVantaEffect(null);
    }
  };

  const loadVantaScripts = () => {
    return new Promise((resolve, reject) => {
      if (window.THREE && window.VANTA) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalScripts = 2;

      const onScriptLoad = () => {
        loadedCount++;
        if (loadedCount === totalScripts) {
          setTimeout(() => resolve(), 100);
        }
      };

      const onScriptError = (error) => {
        reject(error);
      };

      // Load Three.js
      if (!window.THREE) {
        const threeScript = document.createElement('script');
        threeScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js';
        threeScript.onload = onScriptLoad;
        threeScript.onerror = onScriptError;
        document.head.appendChild(threeScript);
      } else {
        onScriptLoad();
      }

      // Load Vanta.js HALO
      if (!window.VANTA) {
        const vantaScript = document.createElement('script');
        vantaScript.src = 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.halo.min.js';
        vantaScript.onload = onScriptLoad;
        vantaScript.onerror = onScriptError;
        document.head.appendChild(vantaScript);
      } else {
        onScriptLoad();
      }
    });
  };

  const toggleSubmenu = (path) => {
    if (!path) return;
    setOpenItems(currentOpenItems => {
      const newOpenItems = {
        ...currentOpenItems,
        [path]: !currentOpenItems[path]
      };
      // Save to localStorage
      localStorage.setItem('sidebar-open-items', JSON.stringify(newOpenItems));
      return newOpenItems;
    });
  };

  const toggleTitleCollapse = (titleName) => {
    if (!titleName) return;
    setCollapsedTitles(currentCollapsed => {
      const newCollapsed = {
        ...currentCollapsed,
        [titleName]: !currentCollapsed[titleName]
      };
      // Save to localStorage
      localStorage.setItem('sidebar-collapsed-titles', JSON.stringify(newCollapsed));
      return newCollapsed;
    });
  };

  // Lấy thông tin platform
  const platformName = getPlatformDisplayName();
  const platformColors = getPlatformColors();
  const currentPlatform = getCurrentPlatform();
  
  // Xác định logo dựa trên platform
  const getLogoPath = () => {
    if (currentPlatform === 'TKT1-landing' || currentPlatform === 'TKT1-app') {
      return '/TKT1_favicon3.png';
    }
    return require("assets/img/TKT1_logo3.png");
  };

  return (
    <div className="sidebar" data-image={image} data-color={color}>
      {/* CSS Animation for magic mode */}
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      
      <div
        className="sidebar-background"
        style={{
          background: magicMode 
            ? 'linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #feca57, #ff9ff3, #54a0ff, #5f27cd, #00d2d3, #ff9f43)'
            : (randomGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
          backgroundSize: magicMode ? '400% 400%' : '100% 100%',
          animation: magicMode ? 'gradientShift 3s ease infinite' : 'none'
        }}
      />
      <div className="sidebar-wrapper">
        <div className="logo d-flex" style={{ 
          paddingTop: '5px',
          position: 'sticky',
          top: '0',
          zIndex: 1000,
          // backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          paddingBottom: '15px',
          marginBottom: '10px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div
            className="simple-text logo-mini mx-1"
            onClick={toggleMagicMode}
            style={{ 
              cursor: process.env.REACT_APP_VANTA === '1' ? 'pointer' : 'default',
              position: 'relative',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (process.env.REACT_APP_VANTA === '1') {
                e.currentTarget.style.transform = 'scale(1.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (process.env.REACT_APP_VANTA === '1') {
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
            title={
              process.env.REACT_APP_VANTA !== '1' 
                ? 'Magic Mode disabled (REACT_APP_VANTA not set)' 
                : magicMode ? 'Click to disable Magic Mode ✨' : 'Click to enable Magic Mode ✨'
            }
          >
            <div className="logo-img" style={{ position: 'relative' }}>
              <img 
                src={getLogoPath()} 
                alt="TKT1 Logo" 
                style={{ 
                  marginTop: '5px', 
                  maxWidth: '50px', 
                  maxHeight: '50px', 
                  borderRadius: '10px',
                  filter: magicMode ? 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))' : 'none',
                  transition: 'filter 0.3s ease'
                }}
              />
              {magicMode && (
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#ffd700',
                  borderRadius: '50%',
                  border: '2px solid white',
                  animation: 'pulse 2s infinite'
                }} />
              )}
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-start',
            marginLeft: '12px' // Tạo khoảng cách với logo
          }}>
            <a className="simple-text" href={`https://${window.location.hostname}`} style={{ textDecoration: 'none', textTransform: 'none' }}>
              {platformName}
            </a>
            {currentCompany && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginTop: '-4px' // Giảm khoảng cách với TKT1
              }}>
                <span 
                  style={{ 
                    fontSize: '11px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    padding: '2px 6px',
                    backgroundColor: currentCompany.is_system ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '12px',
                    border: currentCompany.is_system ? '1px solid rgba(255, 193, 7, 0.4)' : '1px solid rgba(255, 255, 255, 0.2)',
                    fontWeight: '500',
                    display: 'inline-block',
                    cursor: (!currentCompany.is_system && canAccessMyCompany) ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    ...(!currentCompany.is_system && canAccessMyCompany && {
                      ':hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        transform: 'scale(1.05)'
                      }
                    })
                  }}
                  onClick={!currentCompany.is_system ? handleCompanyBadgeClick : undefined}
                  title={!currentCompany.is_system && canAccessMyCompany ? 'Click để xem thông tin công ty' : ''}
                >
                  {currentCompany.is_system ? 'Admin' : currentCompany.name}
                </span>
                
                {/* User Icon */}
                <IconUserCircle 
                  size={16} 
                  color="rgba(255, 255, 255, 0.9)"
                  strokeWidth={1.5}
                  style={{
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = '0.7';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = '1';
                  }}
                  onClick={handleUserIconClick}
                  title="Tài khoản của tôi"
                />
              </div>
            )}
          </div>
        </div>
        
        <Nav>
          {(() => {
            const visibleRoutes = routes.filter(route => !route.hidden && hasAccess(route, userGroups));
            let currentTitle = null;
            const result = [];
            
            visibleRoutes.forEach((prop, key) => {
              if (!prop.redirect) {
                if (prop.isTitle) {
                  currentTitle = prop.name;
                  const isCollapsed = !!collapsedTitles[prop.name];
                  result.push(
                    <li key={key} className="sidebar-title" style={{
                      marginTop: '20px',
                      marginBottom: '5px'
                    }}>
                      <div 
                        className="sidebar-title-content" 
                        style={{
                          // background: 'rgba(255, 255, 255, 0.08)',
                          padding: '10px 8px',
                          margin: '0',
                          // boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                          // backdropFilter: 'blur(5px)',
                          position: 'relative',
                          width: '100%',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                        onClick={() => toggleTitleCollapse(prop.name)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {renderIcon(prop.icon, { className: 'sidebar-icon' })}
                          <p style={{
                            margin: '0',
                            fontWeight: '600',
                            fontSize: '12px',
                            color: 'rgba(255, 255, 255, 0.9)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>{prop.name}</p>
                        </div>
                      {renderIcon(isCollapsed ? IconChevronRight : IconChevronDown, { 
                        size: 16,
                        style: { 
                          color: 'rgba(255, 255, 255, 0.7)',
                          opacity: 0,
                          transition: 'opacity 0.2s ease'
                        },
                        className: 'collapse-icon'
                      })}
                      </div>
                    </li>
                  );
                } else {
                  // Check if current title is collapsed
                  const isTitleCollapsed = currentTitle && !!collapsedTitles[currentTitle];
                  
                  if (!isTitleCollapsed) {
                    if (prop.children && prop.children.length > 0) {
                      const isOpen = !!openItems[prop.path];
                      const filteredChildren = prop.children.filter(child => !child.hidden && hasAccess(child, userGroups));

                      if (filteredChildren.length > 0) {
                        result.push(
                          <li
                            key={key}
                            style={{ marginLeft: '10px' }}
                          >
                            <a
                              href="#"
                              className={`nav-link ${isOpen ? "" : "collapsed"}`}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleSubmenu(prop.path);
                              }}
                              aria-expanded={isOpen}
                            >
                              <span className="sidebar-link-content">
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {renderIcon(prop.icon, { className: 'sidebar-icon' })}
                                    <p style={{ margin: 0 }}>{prop.name}</p>
                                  </div>
                                  {renderIcon(isOpen ? IconChevronDown : IconChevronRight, { 
                                    size: 16,
                                    style: { 
                                      color: 'rgba(255, 255, 255, 0.7)',
                                      opacity: 0,
                                      transition: 'opacity 0.2s ease'
                                    },
                                    className: 'parent-collapse-icon'
                                  })}
                                </div>
                              </span>
                            </a>
                            {isOpen && (
                              <ul className="nav nav-child mt-0">
                                {filteredChildren.map((child, childKey) => (
                                  <li key={childKey}>
                                    <NavLink
                                      to={child.layout + child.path}
                                      className="nav-link"
                                      activeClassName="active"
                                    >
                                      <span className="sidebar-link-content">
                                        {renderIcon(child.icon, { className: 'sidebar-sub-icon', size: 20 })}
                                        <span className="sub-item-text">{child.name}</span>
                                      </span>
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      }
                    } else {
                      result.push(
                        <li
                          className={
                            prop.upgrade
                              ? `active active-pro`
                              : activeRoute(prop.layout + prop.path)
                          }
                          key={key}
                          style={{ marginLeft: '10px' }}
                        >
                          <NavLink
                            to={prop.layout + prop.path}
                            className={`nav-link`}
                            activeClassName="active"
                          >
                            <span className="sidebar-link-content">
                              {renderIcon(prop.icon, { className: 'sidebar-icon' })}
                              <p>{prop.name}</p>
                            </span>
                          </NavLink>
                        </li>
                      );
                    }
                  }
                }
              }
            });
            
            return result;
          })()}
        </Nav>
        {/* <div className="sidebar-footer">
          <NavLink
            to="/admin/my-account"
            className="nav-link"
            activeClassName="active"
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '15px'
            }}
          >
            <span className="sidebar-link-content sidebar-greeting" style={{ width: '100%' }}>
              <div style={{ 
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                width: '100%'
              }}>
                <div className="sidebar-greeting-label-row" style={{ marginBottom: '8px' }}>
                  <span className="sidebar-greeting-label" style={{ fontSize: '14px' }}>
                    {greeting.text} {greeting.icon}
                  </span>
                  <span className="sidebar-greeting-time" style={{ display: 'block', marginTop: '4px' }}>
                    <b>{timeStr}</b>
                  </span>
                </div>
                <span className="sidebar-greeting-name-row" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  {renderIcon(IconUser, { className: 'sidebar-account-icon', size: 20 })}
                  <span className="sidebar-greeting-name" style={{ fontSize: '15px' }}>
                    {user?.full_name || user?.username}
                  </span>               
                </span>
              </div>
            </span>
          </NavLink>
        </div> */}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.authenReducer.user
});

export default connect(mapStateToProps)(Sidebar);
